import { SFX_COLLECTION } from './collections';
import {
  FREE_PAGE_1_SIZE,
  FREE_PAGE_SIZE,
  getFreeQueryOffset,
  PREMIUM_PAGE_SIZE,
  SINGLE_QUERY_PAGE_SIZE,
} from './queries';
import type {
  DropTokensMode,
  SfxSimilarityField,
  SfxSimilarityWeights,
} from './similarity-weights';
import {
  DEFAULT_SFX_FIELD_ORDER,
  DEFAULT_SFX_FIELDS_ENABLED,
  generateSfxQueryByWeights,
} from './similarity-weights';
import type { SfxWithEngagementDocument } from './tracks-with-engagement';
import type { SortOption } from './typesense-sort';
import { getSortBy } from './typesense-sort';

// SFX similarity search field configuration
// SFX uses flat styles (no weightings) plus keywords
export const SFX_SIMILARITY_QUERY_BY = 'keywords, styles, name';

// Weight constants for SFX similarity matching
// Note: SFX team doesn't use style weightings, so all styles are treated equally
const SFX_STYLES_WEIGHT = 5;
const SFX_KEYWORDS_WEIGHT = 1;
const SFX_NAME_WEIGHT = 2;

// Weights for SFX similarity matching
export const SFX_SIMILARITY_QUERY_BY_WEIGHTS = [
  `keywords:${SFX_KEYWORDS_WEIGHT}`,
  `styles:${SFX_STYLES_WEIGHT}`,
  `name:${SFX_NAME_WEIGHT}`,
].join(', ');

const doubleAmpJoin = (...arr: (string | undefined)[]) =>
  arr.filter((item) => Boolean(item)).join(' && ') || undefined;

/**
 * Sanitizes a name string for use in Typesense search queries
 * Removes quotes and dashes to prevent them from being treated as exact match operators
 */
const sanitizeSfxName = (name: string): string => {
  return name
    .replace(/["']/g, '') // Remove all single and double quotes
    .replace(/[-–—]/g, ' ') // Replace dashes (hyphen, en-dash, em-dash) with spaces
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim();
};

// Map SFX field identifiers to document properties
const getSfxFieldValues = (
  sfx: SfxWithEngagementDocument,
  field: SfxSimilarityField
): string[] => {
  switch (field) {
    case 'name':
      return sfx.name ? [sanitizeSfxName(sfx.name)] : [];
    case 'keywords':
      return sfx.keywords ?? [];
    case 'styles':
      return sfx.styles ?? [];
  }
};

/**
 * Extracts keywords and styles from a reference SFX and combines them into a search query string
 * SFX uses flat styles (no weightings) plus keywords
 * Field order and enabled state can be customized for debugging
 */
export const extractKeywordsFromSfx = (
  sfx: SfxWithEngagementDocument,
  fieldOrder: SfxSimilarityField[] = DEFAULT_SFX_FIELD_ORDER,
  fieldsEnabled: Record<
    SfxSimilarityField,
    boolean
  > = DEFAULT_SFX_FIELDS_ENABLED
): string => {
  // Filter to only include enabled fields, then get values in order
  const allTerms = fieldOrder
    .filter((field) => fieldsEnabled[field])
    .flatMap((field) => getSfxFieldValues(sfx, field));

  // Filter out empty strings and join with commas for Typesense multi-term search
  return allTerms.filter(Boolean).join(', ');
};

/**
 * Generates search queries for similar SFX based on a reference SFX's keywords and styles
 * Follows the same pattern as getSimilaritySearches for music
 */
export const getSfxSimilaritySearches = ({
  referenceSfx,
  excludeSfxId,
  sort = 'relevancy',
  fetchFreeAndPremium,
  page = 1,
  sortScoreVersion = 'v1',
  isSubscribedUser = false,
  weightOverrides,
  fieldOrderOverride,
  dropTokensModeOverride,
  fieldsEnabledOverride,
}: {
  referenceSfx: SfxWithEngagementDocument;
  excludeSfxId?: string;
  sort: SortOption;
  fetchFreeAndPremium: boolean;
  page?: number;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  isSubscribedUser?: boolean;
  /** Optional weight overrides for admin debugging */
  weightOverrides?: SfxSimilarityWeights | null;
  /** Optional field order override for admin debugging */
  fieldOrderOverride?: SfxSimilarityField[] | null;
  /** Optional drop tokens mode override for admin debugging */
  dropTokensModeOverride?: DropTokensMode | null;
  /** Optional fields enabled override for admin debugging */
  fieldsEnabledOverride?: Record<SfxSimilarityField, boolean> | null;
}) => {
  const isFirstPage = page === 1;

  // Extract keywords from reference SFX to use as search query
  // Use field order and fields enabled overrides if provided for debugging
  const keywordQuery = extractKeywordsFromSfx(
    referenceSfx,
    fieldOrderOverride ?? DEFAULT_SFX_FIELD_ORDER,
    fieldsEnabledOverride ?? DEFAULT_SFX_FIELDS_ENABLED
  );

  // Use weight overrides if provided, otherwise use default weights
  const queryByWeights = weightOverrides
    ? generateSfxQueryByWeights(weightOverrides)
    : SFX_SIMILARITY_QUERY_BY_WEIGHTS;

  // Use drop tokens mode override if provided
  const dropTokensMode = dropTokensModeOverride ?? 'right_to_left';

  // Build filter to exclude the entire SFX (all variants) from results
  const excludeFilter = excludeSfxId ? `sfx_id:!=${excludeSfxId}` : undefined;

  // Use the sfx.v3 collection for similarity search (no engagement data needed)
  const collection = SFX_COLLECTION;

  // Determine if we have search terms for text matching
  const hasSearchTerms = keywordQuery.length > 0;

  if (fetchFreeAndPremium) {
    const premiumFilter = doubleAmpJoin(excludeFilter, 'is_premium:true');
    const freeFilter = doubleAmpJoin(excludeFilter, 'is_premium:false');

    // Get more free results on the first page, then offset subsequent queries
    const freePerPage = isFirstPage ? FREE_PAGE_1_SIZE : FREE_PAGE_SIZE;
    const freeOffset = getFreeQueryOffset(page);

    return [
      {
        q: keywordQuery || '*',
        query_by: SFX_SIMILARITY_QUERY_BY,
        query_by_weights: queryByWeights,
        collection,
        per_page: PREMIUM_PAGE_SIZE,
        filter_by: premiumFilter,
        sort_by: getSortBy(
          sort,
          true,
          false,
          collection,
          sortScoreVersion,
          hasSearchTerms
        ),
        drop_tokens_threshold: PREMIUM_PAGE_SIZE,
        drop_tokens_mode: dropTokensMode,
        page,
        exclude_fields: 'id',
      },
      {
        q: keywordQuery || '*',
        query_by: SFX_SIMILARITY_QUERY_BY,
        query_by_weights: queryByWeights,
        collection,
        per_page: freePerPage,
        offset: freeOffset,
        filter_by: freeFilter,
        sort_by: getSortBy(
          sort,
          false,
          false,
          collection,
          sortScoreVersion,
          hasSearchTerms
        ),
        drop_tokens_threshold: freePerPage,
        drop_tokens_mode: dropTokensMode,
        exclude_fields: 'id',
      },
    ];
  }

  // Single query mode (for premium users or when not interspersing)
  return [
    {
      q: keywordQuery || '*',
      query_by: SFX_SIMILARITY_QUERY_BY,
      query_by_weights: queryByWeights,
      collection,
      per_page: SINGLE_QUERY_PAGE_SIZE,
      filter_by: excludeFilter,
      sort_by: getSortBy(
        sort,
        isSubscribedUser,
        false,
        collection,
        sortScoreVersion,
        hasSearchTerms
      ),
      drop_tokens_threshold: SINGLE_QUERY_PAGE_SIZE,
      drop_tokens_mode: dropTokensMode,
      page,
      exclude_fields: 'id',
    },
  ];
};
